import { useMemo } from 'react';
import { useCarePlans } from 'src/careflow/ph-core/fhir-client/fhir-react';
import useTable from 'src/hooks/useTable';
import {
  Box,
  Card,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  Grid,
  Typography,
  Table,
} from '@mui/material';
import { TableHeadCustom } from 'src/components/table';
import { isEmpty } from 'lodash';
import { TABLE_HEAD_CARE_PLAN_PATIENT } from 'src/careflow/common/table-head';
import { PatientGenericCaseRow } from './PatientGenericCarePlanRow';
import { CarePlanWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CarePlan';
import {
  PatientGenericCarePlanDto,
  wrappedCarePlanToPatientGenericCarePlanDto,
} from './patientGenericCarePlan.dto';
import { translateTableHead } from 'src/careflow/helpers/common';
import { i18nFunction } from 'src/careflow/ph-core/internationalization/hook/useLocales';

type Props = {
  patientId?: string;
  encounter?: string;
  i18n: i18nFunction;
};

export default function PatientGenericCarePlan({ patientId, encounter, i18n }: Props) {
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({});
  const [carePlans] = useCarePlans({
    filter: {
      patient: patientId,
      ...(encounter && { encounter: encounter }),
    },
    autofetch: !!patientId,
    map: CarePlanWrapper,
  });

  const data = useMemo(() => {
    if (carePlans) {
      return carePlans?.map((x) => wrappedCarePlanToPatientGenericCarePlanDto(x));
    }
    return [];
  }, [carePlans]);

  return (
    <Card style={{ boxShadow: 'none', position: 'static' }} sx={{ m: 1, mt: 0 }}>
      <Grid container>
        <Grid item xs={12}>
          <TableContainer>
            <Table size="small" sx={{ mb: 2 }}>
              <TableHeadCustom
                headLabel={translateTableHead(TABLE_HEAD_CARE_PLAN_PATIENT, i18n, 'crs')}
              />
              <TableBody>
                {!isEmpty(data) ? (
                  data
                    ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row: PatientGenericCarePlanDto) => (
                      <PatientGenericCaseRow key={row?.id} row={row} />
                    ))
                ) : (
                  <TableCell colSpan={TABLE_HEAD_CARE_PLAN_PATIENT?.length}>
                    <Typography variant="body2" align="center">
                      No rows
                    </Typography>
                  </TableCell>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={carePlans?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />
      </Box>
    </Card>
  );
}
